import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAssessmentSession } from "./useAssessmentSession";
import { analyzeAnswersWithAI } from "@/utils/medicalAnalysis";
import { assessmentQuestions } from "@/data/assessmentQuestions";

interface AssessmentState {
  currentQuestionIndex: number;
  answers: Record<string, any>;
  isProcessing: boolean;
}

export const useAssessmentLogic = () => {
  const navigate = useNavigate();
  const { session, saveAssessmentHistory } = useAssessmentSession();

  const [assessmentState, setAssessmentState] = useState<AssessmentState>({
    currentQuestionIndex: 0,
    answers: {},
    isProcessing: false
  });

  const currentQuestion = assessmentQuestions[assessmentState.currentQuestionIndex];
  const progress = ((assessmentState.currentQuestionIndex + 1) / assessmentQuestions.length) * 100;

  const handleAnswer = (answer: string | string[]) => {
    if (!currentQuestion) return;
    
    const newAnswers = {
      ...assessmentState.answers,
      [currentQuestion.id]: answer
    };

    setAssessmentState(prev => ({
      ...prev,
      answers: newAnswers
    }));

    // Check for emergency routing - Route to high-priority results for any emergency option except "none"
    if (currentQuestion.id === "emergency_screening" && typeof answer === 'string' && answer !== "none") {
      // Emergency case - route directly to results with maximum score and high priority
      const emergencyResult = {
        urgency: "emergency" as const,
        category: "Emergency Response",
        recommendation: "Call 999 immediately. This appears to be a medical emergency requiring immediate attention.",
        analysis: "Emergency screening has identified potential life-threatening symptoms that require immediate medical intervention.",
        nextSteps: ["Call 999 immediately", "Do not delay seeking emergency medical care", "Follow emergency services guidance"],
        professionalType: "Emergency Services",
        amIOKScore: 100,
        scientificReasoning: "Emergency symptoms require immediate medical evaluation and treatment.",
        riskFactors: ["Emergency symptoms present"],
        protectiveFactors: []
      };
      
      navigate("/results", { state: { answers: newAnswers, result: emergencyResult } });
      return;
    }

    // Auto-advance to next question after a short delay (except for multiple choice and category-dropdown)
    // Also auto-advance if "None of the above" is selected on systemic_symptoms question
    if (currentQuestion.type !== "multiple" && currentQuestion.type !== "category-dropdown") {
      setTimeout(() => {
        handleNext(newAnswers);
      }, 800);
    } else if (currentQuestion.id === "systemic_symptoms" && Array.isArray(answer) && answer.length === 1 && answer[0] === "None of the above") {
      // Auto-advance when "None of the above" is the only selection
      setTimeout(() => {
        handleNext(newAnswers);
      }, 800);
    }
  };

  const handleNext = async (answersToUse?: Record<string, any>) => {
    const finalAnswers = answersToUse || assessmentState.answers;
    
    if (assessmentState.currentQuestionIndex < assessmentQuestions.length - 1) {
      setAssessmentState(prev => ({
        ...prev,
        currentQuestionIndex: prev.currentQuestionIndex + 1,
        answers: finalAnswers
      }));
    } else {
      // Assessment complete, start processing
      setAssessmentState(prev => ({
        ...prev,
        isProcessing: true
      }));

      try {
        // Navigate immediately with just answers, let ResultsPage1 handle AI scoring
        navigate("/results", { state: { answers: finalAnswers } });
        
        // Save minimal result to history (AI scoring will happen on results page)
        if (session) {
          const minimalResult = {
            urgency: "routine" as const,
            category: "Assessment In Progress",
            recommendation: "AI analysis in progress",
            analysis: "Processing your responses with medical AI",
            nextSteps: ["Analysis in progress"],
            professionalType: "AI Analysis",
            amIOKScore: 0, // Will be replaced by AI scoring
            scientificReasoning: "Analysis in progress",
            riskFactors: [],
            protectiveFactors: []
          };
          await saveAssessmentHistory(finalAnswers, minimalResult);
        }
      } catch (error) {
        console.error('Error during AI analysis:', error);
        // Navigate with a fallback result in case of error
        const fallbackResult = {
          urgency: "routine" as const,
          category: "General Assessment",
          recommendation: "Please consult with a healthcare professional for proper evaluation.",
          analysis: "Assessment completed. Please seek professional medical advice.",
          nextSteps: ["Schedule an appointment with your GP"],
          professionalType: "General Practitioner",
          amIOKScore: 30,
          scientificReasoning: "Analysis pending due to technical issue.",
          riskFactors: [],
          protectiveFactors: []
        };
        
        navigate("/results", { state: { answers: finalAnswers, result: fallbackResult } });
      } finally {
        setAssessmentState(prev => ({
          ...prev,
          isProcessing: false
        }));
      }
    }
  };

  const handlePrevious = () => {
    if (assessmentState.currentQuestionIndex > 0 && !assessmentState.isProcessing) {
      setAssessmentState(prev => ({
        ...prev,
        currentQuestionIndex: prev.currentQuestionIndex - 1
      }));
    }
  };

  const handleBackToHome = () => {
    if (!assessmentState.isProcessing) {
      navigate("/");
    }
  };

  const isAnswered = currentQuestion ? assessmentState.answers[currentQuestion.id] !== undefined : false;

  return {
    assessmentState,
    currentQuestion,
    progress,
    session,
    handleAnswer,
    handleNext,
    handlePrevious,
    handleBackToHome,
    isAnswered
  };
};